import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface TenantRow {
  readonly saasTenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly tenantType: string;
  readonly region: string | null;
  readonly subscriptionPlan: string;
  readonly status: string;
}

const TENANT_TYPES = ['ENTERPRISE', 'BUSINESS', 'PARTNER', 'RESELLER', 'INTERNAL', 'TRIAL'];
const STATUSES = ['REQUESTED', 'UNDER_VERIFICATION', 'PROVISIONING', 'ACTIVE', 'SUSPENDED', 'RESTRICTED', 'EXPIRED', 'TERMINATED', 'ARCHIVED'];
const PAGE = 25;

/**
 * M43 §27 Tenant Management Console (§6 / §7). Platform operators register
 * tenants, browse the estate and drive the lifecycle state machine (verify /
 * provision / activate / suspend / restrict / expire / terminate / archive).
 * Viewing needs TENANT_VIEW; create/verify need TENANT_MANAGE; each lifecycle
 * transition needs its distinct SoD permission (PROVISION / ACTIVATE / SUSPEND /
 * ARCHIVE).
 */
export function TenantManagementConsole(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [tenantName, setTenantName] = useState('');
  const [tenantType, setTenantType] = useState('BUSINESS');
  const [region, setRegion] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter) params.set('tenantType', typeFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: TenantRow[]; total: number }>(`/v1/saas/tenants?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, typeFilter, search, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!tenantName.trim()) { setActionError('A tenant name is required.'); return; }
    try {
      await apiRequest('/v1/saas/tenants', {
        method: 'POST',
        body: { tenantName: tenantName.trim(), tenantType, region: region.trim() || null },
        token,
      });
      setNotice('Tenant created.');
      setTenantName('');
      setRegion('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to create tenants (TENANT_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const transition = async (id: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/saas/tenants/${id}/${verb}`, { method: 'POST', body: {}, token });
      setNotice(`Tenant ${verb} succeeded.`);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(`You are not authorised for this action (${perm} required).`);
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="tenant-management-console">
      <h1>Tenant Management Console</h1>

      <section aria-label="tenant-create">
        <h2>Register Tenant</h2>
        <form aria-label="tenant-form" onSubmit={(e) => void create(e)}>
          <label>Name<input aria-label="field-tenant-name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} /></label>
          <label>Type
            <select aria-label="field-tenant-type" value={tenantType} onChange={(e) => setTenantType(e.target.value)}>
              {TENANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Region<input aria-label="field-region" value={region} onChange={(e) => setRegion(e.target.value)} /></label>
          <button type="submit" aria-label="submit-tenant">Create</button>
        </form>
        {actionError && <p role="alert" aria-label="tenant-action-error">{actionError}</p>}
        {notice && <p aria-label="tenant-notice">{notice}</p>}
      </section>

      <section aria-label="tenant-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Type
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {TENANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="tenant-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="tenant-total">{total} tenant(s).</p>
          <table aria-label="tenant-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Name</th><th scope="col">Type</th><th scope="col">Region</th><th scope="col">Plan</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No tenants.</td></tr>
              ) : rows.map((t) => (
                <tr key={t.saasTenantId} data-tenant={t.saasTenantId}>
                  <td aria-label={`tenant-code-${t.saasTenantId}`}>{t.tenantCode}</td>
                  <td>{t.tenantName}</td>
                  <td>{t.tenantType}</td>
                  <td>{t.region ?? '—'}</td>
                  <td>{t.subscriptionPlan}</td>
                  <td aria-label={`tenant-status-${t.saasTenantId}`}>{t.status}</td>
                  <td>
                    <button type="button" aria-label={`verify-${t.saasTenantId}`} onClick={() => void transition(t.saasTenantId, 'verify', 'SAAS_TENANT_ADMIN_TENANT_MANAGE')}>Verify</button>
                    <button type="button" aria-label={`provision-${t.saasTenantId}`} onClick={() => void transition(t.saasTenantId, 'provision', 'SAAS_TENANT_ADMIN_TENANT_PROVISION')}>Provision</button>
                    <button type="button" aria-label={`activate-${t.saasTenantId}`} onClick={() => void transition(t.saasTenantId, 'activate', 'SAAS_TENANT_ADMIN_TENANT_ACTIVATE')}>Activate</button>
                    <button type="button" aria-label={`suspend-${t.saasTenantId}`} onClick={() => void transition(t.saasTenantId, 'suspend', 'SAAS_TENANT_ADMIN_TENANT_SUSPEND')}>Suspend</button>
                    <button type="button" aria-label={`archive-${t.saasTenantId}`} onClick={() => void transition(t.saasTenantId, 'archive', 'SAAS_TENANT_ADMIN_TENANT_ARCHIVE')}>Archive</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="tenant-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
